/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package ai

import i18n.StringKey
import i18n.tr
import kotlin.math.abs

/**
 * Pure (platform-independent) builder that turns a personalized card + the
 * user's local transactions/goals into a [Wiz3RDetailContent] payload.
 *
 * Each platform's detail screen renders the payload into its native UI.
 * No types from `kotlinx.html`, `androidx.compose`, SwiftUI, or `org.w3c.dom`
 * are referenced — keeps the port to Android/iOS a render-only exercise.
 */
object Wiz3RDetailContentBuilder {

    /**
     * Minimal transaction shape the builder consumes. Each platform supplies
     * an adapter that maps its native transaction type to this interface.
     */
    interface TxnLike {
        val merchant: String
        val date: String              // YYYY-MM-DD
        /** Plaid convention: positive = expense, negative = income. */
        val amount: Double
        /** Raw Plaid primary code (e.g. "FOOD_AND_DRINK"). Used for filtering
         *  against the card's actionData["category"] which carries the same
         *  raw code. */
        val categoryCode: String?
        /** Human-readable, localized label (e.g. "Food and drink"). Used for
         *  display in row subtitles. */
        val categoryLabel: String?
        val assignedGoalId: String?
        val assignedGoalName: String?
        val assignedGoalAmount: Double?
        val excluded: Boolean         // includes split/distributed parents + manual excludes
    }

    interface GoalLike {
        val id: String
        val name: String
        val currentAmount: Double
        val targetAmount: Double
    }

    /**
     * @param cardType matches PersonalizedCardType enum names (string for
     *   platform-independence — server sends the type as text).
     * @param cardActionType matches PersonalizedAdviceCard.actionType.
     * @param cardActionData matches PersonalizedAdviceCard.actionData.
     */
    @Suppress("LongParameterList")
    fun build(
        cardType: String,
        cardActionType: String,
        cardActionData: Map<String, String>,
        currentMonthTxns: List<TxnLike>,
        previousMonthTxns: List<TxnLike>,
        goals: List<GoalLike>,
        metrics: CardMetricsLike?,
    ): Wiz3RDetailContent = Wiz3RDetailContent(
        reveal = buildReveal(cardType, cardActionData, currentMonthTxns, previousMonthTxns, goals),
        reason = buildReason(cardType, metrics),
        realize = buildRealize(cardType, cardActionType, cardActionData),
    )

    /** Subset of the card's metrics used by REASON. Decouples the builder from the SPA's CardMetricsData. */
    interface CardMetricsLike {
        val amount: Double?
        val percentOfIncome: Double?
        val percentChange: Double?
        val savingsRate: Double?
        val previousSavingsRate: Double?
        val goalProgress: Double?
        val category: String?
        val percentOfBudget: Double? get() = null
        val plannedAmount: Double? get() = null
    }

    // ----------------------------------------------------------------------
    // REVEAL
    // ----------------------------------------------------------------------

    private fun buildReveal(
        cardType: String,
        actionData: Map<String, String>,
        currentMonthTxns: List<TxnLike>,
        previousMonthTxns: List<TxnLike>,
        goals: List<GoalLike>,
    ): RevealContent {
        return when (cardType) {
            "SAVINGS_PERSONAL_BEST", "SAVINGS_IMPROVED", "SAVINGS_DROPPED" ->
                revealGoalAllocations(currentMonthTxns, previousMonthTxns)

            "CATEGORY_ALERT", "SPENDING_SPIKE", "BIGGEST_OPPORTUNITY",
            "MONTH_COMPARISON", "CATEGORY_RANK_SHIFT", "WEEKEND_SPENDING",
            "BUDGET_VS_ACTUAL", "CATEGORY_STREAK" ->
                revealCategoryTransactions(actionData["category"], currentMonthTxns)

            "GOAL_ON_TRACK", "GOAL_BEHIND", "GOAL_MILESTONE",
            "GOAL_ALMOST_COMPLETE", "GOAL_PROJECTION" ->
                revealGoalProgress(actionData["goalId"], currentMonthTxns, previousMonthTxns, goals)

            "TOP_MERCHANT" ->
                revealMerchantTransactions(actionData["merchants"], currentMonthTxns)

            "SUBSCRIPTION_CREEP", "UPCOMING_BILLS" ->
                // Recurring bills/subs are billed on arbitrary cycle dates —
                // many of the listed merchants haven't fired this calendar
                // month yet. Show the most recent charge by widening the pool
                // to current + previous month so the user actually sees the
                // recent history of the bills surfaced in the card.
                revealRecurringMerchants(actionData["merchants"], currentMonthTxns + previousMonthTxns)

            else -> RevealContent(
                heading = StringKey.WIZ3R_REVEAL_GENERIC_HEADING.tr(),
                emptyMessage = StringKey.WIZ3R_REVEAL_GENERIC_EMPTY.tr(),
            )
        }
    }

    private fun revealGoalAllocations(
        currentMonthTxns: List<TxnLike>,
        previousMonthTxns: List<TxnLike>,
    ): RevealContent {
        val current = currentMonthTxns.filter { (it.assignedGoalAmount ?: 0.0) > 0 && !it.excluded }
        val previous = previousMonthTxns.filter { (it.assignedGoalAmount ?: 0.0) > 0 && !it.excluded }
        val currentTotal = current.sumOf { it.assignedGoalAmount ?: 0.0 }
        val previousTotal = previous.sumOf { it.assignedGoalAmount ?: 0.0 }
        val rows = current
            .sortedByDescending { it.assignedGoalAmount ?: 0.0 }
            .take(10)
            .map { txn ->
                DetailRow(
                    title = txn.merchant.ifBlank { txn.categoryLabel ?: "Transaction" },
                    subtitle = "${txn.date}  ·  ${txn.assignedGoalName ?: "Goal"}",
                    amountText = formatMoney(txn.assignedGoalAmount ?: 0.0),
                    amountColorHint = INCOME_GREEN,
                )
            }
        return RevealContent(
            heading = StringKey.WIZ3R_REVEAL_GOAL_TXNS_HEADING.tr(),
            subheading = StringKey.WIZ3R_REVEAL_GOAL_TXNS_SUBHEADING.tr(),
            rows = rows,
            emptyMessage = if (rows.isEmpty()) StringKey.WIZ3R_REVEAL_GOAL_TXNS_EMPTY.tr() else null,
            comparison = RevealComparison(
                currentLabel = StringKey.WIZ3R_REVEAL_THIS_MONTH.tr(),
                currentValue = formatMoney(currentTotal),
                previousLabel = StringKey.WIZ3R_REVEAL_LAST_MONTH.tr(),
                previousValue = formatMoney(previousTotal),
                deltaText = formatDelta(currentTotal, previousTotal),
            ),
        )
    }

    private fun revealCategoryTransactions(
        category: String?,
        currentMonthTxns: List<TxnLike>,
    ): RevealContent {
        // Filter on the RAW Plaid primary code — the card's actionData stores
        // "FOOD_AND_DRINK", not "Food and drink", so we match code-to-code.
        val filtered = currentMonthTxns.filter {
            !it.excluded && it.amount > 0 && (category == null || it.categoryCode.equals(category, ignoreCase = true))
        }
        val total = filtered.sumOf { it.amount }
        val rows = filtered
            .sortedByDescending { it.amount }
            .take(10)
            .map { txn ->
                DetailRow(
                    title = txn.merchant.ifBlank { txn.categoryLabel ?: "Transaction" },
                    subtitle = "${txn.date}  ·  ${txn.categoryLabel ?: "Uncategorized"}",
                    amountText = formatMoney(txn.amount),
                )
            }
        // Humanize the raw code for the heading so the user sees "Food and drink"
        // instead of "FOOD_AND_DRINK".
        val displayCategory = category?.let { humanizeCategoryCode(it) }
        return RevealContent(
            heading = if (displayCategory != null) StringKey.WIZ3R_REVEAL_CATEGORY_HEADING.tr(displayCategory)
                      else StringKey.WIZ3R_REVEAL_CATEGORY_HEADING_GENERIC.tr(),
            subheading = StringKey.WIZ3R_REVEAL_CATEGORY_SUBHEADING.tr(),
            rows = rows,
            emptyMessage = if (rows.isEmpty()) StringKey.WIZ3R_REVEAL_CATEGORY_EMPTY.tr() else null,
            comparison = RevealComparison(
                currentLabel = StringKey.WIZ3R_REVEAL_CATEGORY_TOTAL_THIS_MONTH.tr(),
                currentValue = formatMoney(total),
                previousLabel = StringKey.WIZ3R_REVEAL_CATEGORY_SHOWN.tr(),
                previousValue = "${filtered.size.coerceAtMost(10)} / ${filtered.size}",
            ),
        )
    }

    /** Light-weight raw-code → human-readable transformation used only for
     *  the section headings. Per-transaction subtitles use the localized
     *  [TxnLike.categoryLabel] which comes from shared CategoryUtils. */
    private fun humanizeCategoryCode(raw: String): String =
        raw.replace("_", " ").lowercase().replaceFirstChar { it.uppercase() }

    private fun revealGoalProgress(
        goalId: String?,
        currentMonthTxns: List<TxnLike>,
        previousMonthTxns: List<TxnLike>,
        goals: List<GoalLike>,
    ): RevealContent {
        val goal = goals.firstOrNull { it.id == goalId } ?: goals.firstOrNull()
        val relevant = (currentMonthTxns + previousMonthTxns)
            .filter { !it.excluded && it.assignedGoalId == goalId && (it.assignedGoalAmount ?: 0.0) > 0 }
        val rows = relevant
            .sortedByDescending { it.date }
            .take(10)
            .map { txn ->
                DetailRow(
                    title = txn.merchant.ifBlank { "Contribution" },
                    subtitle = txn.date,
                    amountText = formatMoney(txn.assignedGoalAmount ?: 0.0),
                    amountColorHint = INCOME_GREEN,
                )
            }
        return RevealContent(
            heading = if (goal != null) StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_HEADING.tr(goal.name)
                      else StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_HEADING_GENERIC.tr(),
            subheading = StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_SUBHEADING.tr(),
            rows = rows,
            emptyMessage = if (rows.isEmpty()) StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_EMPTY.tr() else null,
            comparison = if (goal != null) RevealComparison(
                currentLabel = StringKey.WIZ3R_REVEAL_CURRENT_BALANCE.tr(),
                currentValue = formatMoney(goal.currentAmount),
                previousLabel = StringKey.WIZ3R_REVEAL_TARGET.tr(),
                previousValue = formatMoney(goal.targetAmount),
                deltaText = if (goal.targetAmount > 0) {
                    "${formatPercent(analytics.goalProgressPercent(goal.currentAmount, goal.targetAmount))} ${StringKey.WIZ3R_REVEAL_FUNDED.tr()}"
                } else null,
            ) else null,
        )
    }

    private fun revealMerchantTransactions(merchants: String?, txns: List<TxnLike>): RevealContent {
        val merchantList = merchants
            ?.split(",")
            ?.map { it.trim().lowercase() }
            ?.filter { it.isNotEmpty() }
            ?: emptyList()
        val filtered = txns.filter {
            !it.excluded && it.amount > 0 &&
                (merchantList.isEmpty() || merchantList.any { m -> it.merchant.lowercase().contains(m) })
        }
        val rows = filtered.sortedByDescending { it.amount }.take(10).map { txn ->
            DetailRow(
                title = txn.merchant.ifBlank { txn.categoryLabel ?: "Transaction" },
                subtitle = "${txn.date}  ·  ${txn.categoryLabel ?: "Uncategorized"}",
                amountText = formatMoney(txn.amount),
            )
        }
        return RevealContent(
            heading = StringKey.WIZ3R_REVEAL_MERCHANT_HEADING.tr(),
            rows = rows,
            emptyMessage = if (rows.isEmpty()) StringKey.WIZ3R_REVEAL_NO_TRANSACTIONS.tr() else null,
        )
    }

    private fun revealRecurringMerchants(merchants: String?, txns: List<TxnLike>): RevealContent {
        return revealMerchantTransactions(merchants, txns).copy(
            heading = StringKey.WIZ3R_REVEAL_RECURRING_HEADING.tr(),
            subheading = StringKey.WIZ3R_REVEAL_RECURRING_SUBHEADING.tr(),
        )
    }

    // ----------------------------------------------------------------------
    // REASON
    // ----------------------------------------------------------------------

    private fun buildReason(cardType: String, metrics: CardMetricsLike?): ReasonContent {
        val lines = mutableListOf<MathLine>()

        metrics?.savingsRate?.let { rate ->
            lines += MathLine(label = StringKey.WIZ3R_REASON_SAVINGS_RATE_THIS.tr(), result = formatPercent(rate))
        }
        metrics?.previousSavingsRate?.let { rate ->
            // SAVINGS_PERSONAL_BEST puts the previous *record* month here, not
            // last month's rate — calling it "Savings rate last month" states
            // something false whenever the record wasn't set last month.
            val prevLabel = when (cardType) {
                "SAVINGS_PERSONAL_BEST" -> StringKey.WIZ3R_REASON_PREVIOUS_BEST
                else -> StringKey.WIZ3R_REASON_SAVINGS_RATE_PREV
            }
            lines += MathLine(label = prevLabel.tr(), result = formatPercent(rate))
        }
        metrics?.percentOfIncome?.let { pct ->
            lines += MathLine(label = StringKey.WIZ3R_REASON_PERCENT_OF_INCOME.tr(), result = formatPercent(pct))
        }
        metrics?.percentOfBudget?.let { pct ->
            lines += MathLine(label = StringKey.WIZ3R_REASON_PERCENT_OF_BUDGET.tr(), result = formatPercent(pct))
        }
        metrics?.plannedAmount?.let { amt ->
            lines += MathLine(label = StringKey.WIZ3R_REASON_PLANNED_AMOUNT.tr(), result = formatMoney(amt))
        }
        metrics?.percentChange?.let { pct ->
            lines += MathLine(
                label = StringKey.WIZ3R_REASON_CHANGE_VS_PRIOR.tr(),
                result = (if (pct >= 0) "+" else "") + formatPercent(pct),
            )
        }
        metrics?.amount?.let { amt ->
            // CardMetrics.amount is a different quantity on every card type —
            // a 12-month projection here, a goal balance there, a category
            // spend elsewhere. Rendering all of them as a bare "Amount" is how
            // a $2,080/month saver ends up reading "Amount $24,957" and
            // reasonably concluding the number is wrong. Name what it is.
            val amountLabel = when (cardType) {
                "SAVINGS_PERSONAL_BEST" -> StringKey.WIZ3R_REASON_YEARLY_PROJECTION
                "SAVINGS_IMPROVED" -> StringKey.WIZ3R_REASON_EXTRA_SAVED
                "GOAL_MILESTONE" -> StringKey.WIZ3R_REASON_SAVED_SO_FAR
                "GOAL_ALMOST_COMPLETE", "GOAL_PROJECTION" -> StringKey.WIZ3R_REASON_REMAINING_TO_GOAL
                else -> StringKey.WIZ3R_REASON_AMOUNT
            }
            lines += MathLine(label = amountLabel.tr(), result = formatMoney(amt))
        }
        metrics?.goalProgress?.let { p ->
            // goalProgress is ALREADY in percent form (0-100). Same bug
            // we just stamped out in Wiz3RAnthropicClient.metricsLinesFor.
            lines += MathLine(label = StringKey.WIZ3R_REASON_GOAL_FUNDED.tr(), result = formatPercent(p))
        }

        val explainer = when (cardType) {
            "SAVINGS_PERSONAL_BEST", "SAVINGS_IMPROVED", "SAVINGS_DROPPED" ->
                StringKey.WIZ3R_REASON_EXPLAINER_SAVINGS.tr()
            "CATEGORY_ALERT", "SPENDING_SPIKE", "BIGGEST_OPPORTUNITY" ->
                StringKey.WIZ3R_REASON_EXPLAINER_CATEGORY.tr()
            "GOAL_PROJECTION" ->
                StringKey.WIZ3R_REASON_EXPLAINER_PROJECTION.tr()
            else -> null
        }

        return ReasonContent(mathLines = lines, explainer = explainer)
    }

    // ----------------------------------------------------------------------
    // REALIZE
    // ----------------------------------------------------------------------

    /**
     * Standalone realize-section build for surfaces that only need the action
     * list + intro (e.g. mobile bottom sheets that render the card's existing
     * narrative text but want the same dual-action UI as the web detail page).
     * Keeps mobile from having to duplicate the action-mapping logic.
     */
    fun buildRealizeOnly(
        cardType: String,
        cardActionType: String,
        cardActionData: Map<String, String>,
    ): RealizeContent = buildRealize(cardType, cardActionType, cardActionData)

    private fun buildRealize(
        cardType: String,
        cardActionType: String,
        cardActionData: Map<String, String>,
    ): RealizeContent {
        val actions = mutableListOf<RealizeAction>()

        // Primary: the card's own action.
        when (cardActionType) {
            "NAVIGATE_TRANSACTIONS" -> actions += RealizeAction(
                label = StringKey.WIZ3R_REALIZE_REVIEW_TXNS.tr(),
                actionType = "NAVIGATE_TRANSACTIONS",
                actionData = cardActionData,
                primary = true,
            )
            "NAVIGATE_CATEGORY" -> actions += RealizeAction(
                label = StringKey.WIZ3R_REALIZE_SEE_CATEGORY.tr(),
                actionType = "NAVIGATE_CATEGORY",
                actionData = cardActionData,
                primary = true,
            )
            "NAVIGATE_GOAL" -> actions += RealizeAction(
                label = StringKey.WIZ3R_REALIZE_OPEN_GOAL.tr(),
                actionType = "NAVIGATE_GOAL",
                actionData = cardActionData,
                primary = true,
            )
            "CREATE_GOAL" -> actions += RealizeAction(
                label = StringKey.WIZ3R_REALIZE_CREATE_GOAL.tr(),
                actionType = "CREATE_GOAL",
                primary = true,
            )
            "REVIEW_SUBSCRIPTIONS" -> actions += RealizeAction(
                label = StringKey.WIZ3R_REALIZE_REVIEW_SUBSCRIPTIONS.tr(),
                actionType = "REVIEW_SUBSCRIPTIONS",
                actionData = cardActionData,
                primary = true,
            )
            "NAVIGATE_GUIDE" -> actions += RealizeAction(
                label = StringKey.WIZ3R_REALIZE_READ_GUIDE.tr(),
                actionType = "NAVIGATE_GUIDE",
                primary = true,
            )
        }

        // Secondary: card-type-appropriate complement.
        when (cardType) {
            "SAVINGS_PERSONAL_BEST", "SAVINGS_IMPROVED", "SAVINGS_DROPPED" -> {
                actions += RealizeAction(label = StringKey.WIZ3R_REALIZE_OPEN_GOALS.tr(), actionType = "NAVIGATE_GUIDE_GOALS")
            }
            "CATEGORY_ALERT", "SPENDING_SPIKE", "BIGGEST_OPPORTUNITY",
            "MONTH_COMPARISON", "CATEGORY_RANK_SHIFT" -> {
                actions += RealizeAction(
                    label = StringKey.WIZ3R_REALIZE_SET_BUDGET.tr(),
                    actionType = "NAVIGATE_BUDGET",
                    // Inherit the primary's category payload so the Budget tab
                    // knows which row to focus + highlight.
                    actionData = cardActionData,
                )
            }
            "GOAL_ON_TRACK", "GOAL_BEHIND", "GOAL_MILESTONE",
            "GOAL_ALMOST_COMPLETE", "GOAL_PROJECTION" -> {
                actions += RealizeAction(label = StringKey.WIZ3R_REALIZE_TAG_MORE_TRANSFERS.tr(), actionType = "NAVIGATE_TRANSACTIONS")
            }
            else -> {}
        }

        val intro = when (cardType) {
            "SAVINGS_PERSONAL_BEST" -> StringKey.WIZ3R_REALIZE_INTRO_PERSONAL_BEST.tr()
            "SAVINGS_DROPPED" -> StringKey.WIZ3R_REALIZE_INTRO_SAVINGS_DROP.tr()
            "CATEGORY_ALERT", "SPENDING_SPIKE" -> StringKey.WIZ3R_REALIZE_INTRO_CATEGORY.tr()
            "GOAL_ALMOST_COMPLETE" -> StringKey.WIZ3R_REALIZE_INTRO_ALMOST_COMPLETE.tr()
            "GOAL_BEHIND" -> StringKey.WIZ3R_REALIZE_INTRO_BEHIND.tr()
            else -> null
        }

        return RealizeContent(intro = intro, actions = actions)
    }

    // ----------------------------------------------------------------------
    // Formatting helpers (kept here to avoid a separate util file)
    // ----------------------------------------------------------------------

    private const val INCOME_GREEN = "#4caf50"

    private fun formatMoney(amount: Double): String {
        // HALF_UP rounding to match the server's "%.0f".format() — keeps the
        // REVEAL data block in sync with the LLM-cited number that came from
        // the same Double. Without this we get $1,264 (truncate) vs $1,265
        // (round) on the same value.
        val absInt = kotlin.math.round(abs(amount)).toLong()
        // Manual thousands separator — avoids Locale-dependent formatting.
        val withSeparators = absInt
            .toString()
            .reversed()
            .chunked(3)
            .joinToString(",")
            .reversed()
        val sign = if (amount < 0) "-" else ""
        return "$sign\$$withSeparators"
    }

    private fun formatPercent(pct: Double): String {
        val rounded = ((pct * 10).toLong()) / 10.0
        // Drop trailing ".0" for integer percents.
        return if (rounded == rounded.toLong().toDouble()) "${rounded.toLong()}%" else "$rounded%"
    }

    private fun formatDelta(current: Double, previous: Double): String {
        val delta = current - previous
        val sign = if (delta >= 0) "+" else "−"
        return "$sign${formatMoney(abs(delta))} vs last month"
    }
}
